import React, { useCallback, useState } from 'react';

import cx from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import s from './Status.module.css';
import { BrokenIcon, DecommissionIcon, FreeIcon, InWorkIcon } from './StatusImage';
import { sendChangeInventoryStatusRequest, updateFreeDate } from '../../../store/slices/boardSlice';
import { setNeedUpdateTotalInfo } from '../../../store/slices/totalInfoSlice';
import { getWarehouseTotalInfoRequest } from '../../../store/slices/warehouseTotalInfoSlice';
import { DateInput } from '../../DateInput/DateInput';
import { useInventoryStatusesData } from '../../Inventory/inventoryStatusesData';

export const Status = ({
    warehouseId,
    inventoryGuid,
    freeDate,
    status,
    setNewInventoryLog,
    setInventoryData,
    inventoryData,
    hasFullAccess,
    deleteInventory,
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const date = freeDate ? moment(freeDate).format('DD.MM.YYYY') : '';

    const updateFreeDateInput = useCallback((newDate) => {
        dispatch(
            updateFreeDate({
                warehouseId,
                inventoryGuid,
                freeDate: newDate || null,
            })
        );
    }, []);

    const { inventoryStatusesData } = useInventoryStatusesData();

    const initialStatus = inventoryStatusesData.find((item) => item.status === status).status;
    const [isInWorkMode, setIsInWorkMode] = useState(status === 0);
    const [isStatusDisable, setIsStatusDisable] = useState(false);
    const [pressedButton, setPressedButton] = useState(initialStatus);
    const tomorrow = moment().add(1, 'days').format('DD.MM.YYYY');
    const url = helpable.replaceUrlParameters(routable.getRoute('updateInventoryFreeDate'), {
        _guid_: inventoryGuid,
    });

    const changeStatus = useCallback(
        (newStatus) => {
            if (newStatus === pressedButton) {
                return;
            }
            setIsStatusDisable(true);
            const payload = { warehouseId, inventoryGuid, status: newStatus };
            const changeStatusServerResponse = dispatch(sendChangeInventoryStatusRequest(payload));
            changeStatusServerResponse
                .then((response) => response.json())
                .then((data) => {
                    setNewInventoryLog(data.response);
                    setIsStatusDisable(false);
                    dispatch(setNeedUpdateTotalInfo(true));
                    dispatch(getWarehouseTotalInfoRequest(warehouseId));
                });
            setPressedButton(newStatus);
            setInventoryData((prevState) => ({ ...prevState, status: newStatus }));

            if (newStatus === 0) {
                setIsInWorkMode(true);
            } else if (newStatus === 3) {
                setIsInWorkMode(false);
            } else {
                setIsInWorkMode(false);
                updateFreeDateInput();
            }
        },
        [warehouseId, inventoryGuid, pressedButton]
    );

    const dateOptions = {
        startDate: date || tomorrow,
        singleDatePicker: true,
        showDropdowns: true,
        minDate: tomorrow,
    };
    if (!freeDate) {
        dateOptions.autoUpdateInput = false;
    }

    return (
        <div className={s.statusWrapper}>
            <div className={s.status}>
                <h4 className={s.subTitle}>{t('Status.return.statusWrapper.subTitle')}</h4>
                <ul className={s.statusList}>
                    {inventoryStatusesData.map((button) => {
                        const style = {};
                        if (pressedButton === button.status) {
                            style.backgroundColor = button.color;
                            style.color = '#fff';
                        }

                        let icon;
                        const className = cx(s.statusIcon, {
                            [s.activeIcon]: pressedButton === button.status,
                        });

                        if (button.status === 0) {
                            icon = <InWorkIcon className={className} />;
                        }
                        if (button.status === 1) {
                            icon = <FreeIcon className={className} />;
                        }
                        if (button.status === 2) {
                            icon = <BrokenIcon className={className} />;
                        }
                        if (button.status === 3) {
                            icon = <DecommissionIcon className={className} />;
                        }

                        return (
                            <li
                                data-id="status-button"
                                data-status={button.status}
                                className={s.statusItem}
                                key={button.status}
                                onClick={() => {
                                    if (!isStatusDisable) {
                                        changeStatus(button.status);
                                    }
                                }}
                                style={style}
                            >
                                {icon}
                                <span className={s.statusText}>{button.text}</span>
                            </li>
                        );
                    })}
                </ul>
            </div>
            <div>
                {isInWorkMode && (
                    <div className={s.beFree}>
                        <h4 className={s.subTitle}>{t('Status.return.isInWorkMode.subTitle')}</h4>
                        <div className={s.beFreeDate}>
                            <DateInput
                                date={date}
                                inventoryGuid={inventoryGuid}
                                warehouseId={warehouseId}
                                requestUrl={url}
                                parameterName="date"
                                callback={updateFreeDateInput}
                                options={dateOptions}
                            />
                        </div>
                    </div>
                )}
                {hasFullAccess && (
                    <div className={s.removeButtonWrapper}>
                        {inventoryData.status === 3 && (
                            <div
                                data-id="remove-inventory-button"
                                className={s.removeButton}
                                onClick={() => {
                                    deleteInventory();
                                }}
                            >
                                {t('Status.removeBtn.text')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
